import * as React from 'react'

import {
  Box,
  Button,
  Card,
  Icon,
  ProgressBar,
  Spinner,
  Text,
} from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import CancelUploadProcess from 'modules/documents/components/BulkFiles/CancelUploadProcess'
import { UploadProcess } from 'modules/documents/hooks/useGetUploadProcess'

interface Props {
  uploadProcess: UploadProcess
  processedDocuments: number | null | undefined
  expectedDocuments: number | null | undefined
  processedPages?: number | null | undefined
  expectedPages?: number | null | undefined
  onCancel: () => void
}

interface SectionText {
  title: string
  description: string
  cancelText: string
  cancelType: 'primary' | 'secondary'
}

const getConfig = (uploadProcess: UploadProcess): SectionText => {
  const titleText = i18n.t('split_documents.upload_process.title.in_progress_v2')
  const cancelText = i18n.t('split_documents.upload_process.cancel.in_progress_v2')

  const textSettings: { [key: string]: SectionText | undefined } = {
    in_progress: {
      title: titleText,
      description: i18n.t('split_documents.upload_process.description.in_progress'),
      cancelText: cancelText,
      cancelType: 'secondary',
    },
    error: {
      title: i18n.t('split_documents.upload_process.title.error'),
      description:
        uploadProcess.errorMessage || i18n.t('split_documents.upload_process.description.error'),
      cancelText: i18n.t('split_documents.upload_process.cancel.error'),
      cancelType: 'primary',
    },
  }

  return (
    textSettings[uploadProcess.status] || {
      title: '',
      description: '',
      cancelText: '',
      cancelType: 'primary',
    }
  )
}

const UploadProcessCard = ({
  uploadProcess,
  processedDocuments = 0,
  expectedDocuments = 0,
  processedPages = 0,
  expectedPages = 0,
  onCancel,
}: Props) => {
  const [showCancelModal, setShowCancelModal] = React.useState(false)

  const hasErrors = uploadProcess.status === 'error'

  const hasCount =
    !hasErrors &&
    Number.isInteger(processedDocuments) &&
    Number.isInteger(expectedDocuments) &&
    expectedDocuments != null &&
    expectedDocuments > 0

  const hasPages =
    !hasErrors &&
    Number.isInteger(processedPages) &&
    Number.isInteger(expectedPages) &&
    expectedPages != null &&
    expectedPages > 1

  const { title, description, cancelText, cancelType } = getConfig(uploadProcess)
  const infoText = i18n.t('split_documents.upload_process.cancel_modal.info_v2')

  return (
    <>
      <Card>
        <Card.Content>
          <Box alignItems="center" width="full" padding="s24" paddingBottom="s12">
            <Box
              justifyContent="center"
              minHeight="s240"
              width="s400"
              alignItems="center"
              textAlign="center"
              gap="s8"
            >
              <Icon.Large icon="document-folder-files" />

              <Text size="400" weight="semibold">
                {title}
              </Text>

              <Text size="300">{description}</Text>

              {hasCount && expectedDocuments ? (
                <>
                  <Box paddingTop="s8" gap="s8">
                    <Text size="200">
                      {i18n.t('split_documents.upload_process.progress', {
                        amount: processedDocuments,
                        total: expectedDocuments,
                      })}
                      {hasPages && processedPages && expectedPages && (
                        <>
                          <Box paddingTop="s8"></Box>
                          {i18n.t('split_documents.upload_process.progress_pages', {
                            amount: processedPages,
                            total: expectedPages,
                          })}
                          <Box />
                        </>
                      )}
                    </Text>
                  </Box>
                  <Box paddingY="s8" width="s200">
                    <ProgressBar
                      amount={processedDocuments || 0}
                      total={expectedDocuments}
                      size="large"
                    />
                  </Box>
                </>
              ) : (
                !hasErrors && (
                  <Box paddingY="s4">
                    <Spinner />
                  </Box>
                )
              )}

              <Box paddingTop="s8">
                <Button type={cancelType} onClick={() => setShowCancelModal(true)}>
                  {cancelText}
                </Button>
              </Box>
            </Box>
          </Box>
        </Card.Content>
      </Card>

      {showCancelModal && (
        <CancelUploadProcess
          uploadProcess={uploadProcess}
          onClose={() => setShowCancelModal(false)}
          onCancel={onCancel}
          infoText={infoText}
        />
      )}
    </>
  )
}

export default UploadProcessCard
